import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Breadcrumbs, Typography, Box, IconButton } from "@mui/material";
import {
  Home,
  ChevronLeftRounded,
  ChevronRightRounded,
} from "@mui/icons-material";
import { useUI } from "../../contexts/UIContext";

/* -----------------------------
   Types
----------------------------- */

export interface BreadcrumbItem {
  label: string;
  to?: string;
  state?: any;
}

interface DashboardBreadcrumbsProps {
  items?: BreadcrumbItem[];
  rightAction?: React.ReactNode;
}

/* -----------------------------
   Component
----------------------------- */

const DashboardBreadcrumbs: React.FC<DashboardBreadcrumbsProps> = ({
  items = [],
  rightAction,
}) => {
  const { isDrawerOpen, toggleDrawer } = useUI();
  const navigate = useNavigate();
  
  // ✅ No longer filtering items to allow competition name to appear
  const filteredItems = items;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        mb: 0.5,
      }}
    >
      {/* Drawer Toggle */}
      <IconButton
        size="small"
        sx={{ mr: 1, borderRadius: 2, background: "#323638" }}
        onClick={toggleDrawer}
      >
        {isDrawerOpen ? <ChevronLeftRounded /> : <ChevronRightRounded />}
      </IconButton>

      {/* Breadcrumbs */}
      <Breadcrumbs
        aria-label="breadcrumb"
        sx={{
          fontSize: "1rem",
          pl: 0,
          pt: 0,
        }}
      >
        {/* Home */}
        <Link to="/competitions" style={{ textDecoration: "none" }}>
          <Home
            sx={{
              color: "text.primary",
              fontSize: "1.2em",
              display: "flex",
              alignItems: "center",
              "&:hover": {
                opacity: 0.7, // ✅ Just reduce opacity on hover
              },
            }}
          />
        </Link>

        {/* Dynamic items - filtered */}
        {filteredItems.map((item, index) =>
          item.to ? (
            <Box
              key={index}
              component="span"
              onClick={() => navigate(item.to!, { state: item.state })}
              sx={{
                cursor: "pointer",
                textDecoration: "none",
              }}
            >
              <Typography
                color="text.primary"
                sx={{
                  "&:hover": {
                    opacity: 0.7, // ✅ Just reduce opacity, no color change or underline
                  },
                }}
              >
                {item.label}
              </Typography>
            </Box>
          ) : (
            <Typography key={index} component="div" color="text.primary">
              {item.label}
            </Typography>
          )
        )}
      </Breadcrumbs>

      {/* Right Action */}
      {rightAction && (
        <Box sx={{ ml: "auto", display: "flex", alignItems: "center" }}>
          {rightAction}
        </Box>
      )}
    </Box>
  );
};

export default DashboardBreadcrumbs;